import { useMemo } from 'react';

import useGroupedRoomOptions from '@shared/v2/hooks/useGroupedRoomOptions';
import useSearchQuery from '@shared/v2/hooks/useSearchQuery';
import searchQueriesMatch from '@shared/v2/utils/searchQueriesMatch';

import useGetPackageOffers from '@/components/PackagePage/hooks/useGetPackageOffers';
import useProcuredOffer from '@/components/PackagePage/hooks/useProcuredOffer';
import usePropertyContent from '@/hooks/usePropertyContent';
import { UI } from '@/v2/types';
import getUniquePromoCodes from './utils/getUniquePromoCodes';

const useRooms = () => {
  const propertyContent: UI.Property = usePropertyContent();
  const procuredOffer: UI.ProcuredOffer = useProcuredOffer();
  const { searchQuery, isReady } = useSearchQuery();
  const {
    searchQuery: searchVariables,
    packageOffers,
    loading,
    flightOptions,
    //TODO: would be nice to implement this in TS to get proper typing
  } = useGetPackageOffers() as {
    searchQuery: any;
    packageOffers: UI.PackageOffer[] | undefined;
    flightOptions: UI.FlightOptions | undefined;
    loading: boolean;
  };

  const { originCode, packageOption } = searchQuery || {};
  const isPreSearch = !searchVariables;

  const roomOptions = useGroupedRoomOptions({
    packageOffers,
    procuredOfferRooms: procuredOffer?.rooms,
    roomTypes: propertyContent?.roomTypes,
    selectedOrigin: originCode,
    selectedInclusionSetId: packageOption,
  });

  const flightPromoCodes = useMemo(
    () => getUniquePromoCodes(flightOptions),
    [flightOptions],
  );

  // The loading value returned from useGetPackageOffers is not accurate,
  // so this slight hack is to help provide a more accurate loading/search state.
  const hasSearchCompleted = useMemo(() => {
    return searchQueriesMatch({
      searchQueries: [searchQuery, searchVariables],
      paramsToCompare: [
        'departureDate',
        'returnDate',
        'adults',
        'children',
        'infants',
        'originCode',
      ],
    });
  }, [searchQuery, searchVariables]);

  const isLoading = useMemo(() => {
    if (isPreSearch) return false;
    return loading || !isReady || !hasSearchCompleted;
  }, [isPreSearch, loading, isReady, hasSearchCompleted]);

  return {
    roomOptions,
    loading: isLoading,
    searchQuery,
    isPreSearch,
    packageOffers,
    flightOptions,
    flightPromoCodes,
  };
};

export default useRooms;
